import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { FileText, Upload, X as XIcon } from "lucide-react";
import { UploadProgressBar } from "../../components/ui/UploadProgressBar";
import { formatRuPhone, RU_PHONE_PLACEHOLDER } from "../../utils/phone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const JournalistVerification = ({ user, showToast, onBack }) => {
 const [formData, setFormData] = useState({
 portfolio_urls: [""],
 media_organization: "",
 press_card_number: "",
 contact_person: "",
 contact_phone: "",
 contact_email: "",
 specializations: [],
 equipment: [],
 work_region: ""
 });

 const SPECIALIZATION_OPTIONS = [
 "Репортаж с места событий",
 "Интервью",
 "Съёмка мероприятий",
 "Аэросъёмка (дрон)",
 "Стрим / прямой эфир",
 "Видеоотчёт",
 "Документальная съёмка"
 ];

 const EQUIPMENT_OPTIONS = [
 "Камера 12 МП+",
 "4К-видео",
 "Дрон",
 "Стабилизатор (гимбал)",
 "Внешний микрофон",
 "Накамерный свет",
 "Петличный микрофон"
 ];

 const toggleArrayItem = (field, value) => {
 setFormData(prev => ({
 ...prev,
 [field]: prev[field].includes(value)
 ? prev[field].filter(v =>v !== value)
 : [...prev[field], value]
 }));
 };
 const [loading, setLoading] = useState(false);
 const [uploadLoading, setUploadLoading] = useState(false);
 const [progressItems, setProgressItems] = useState([]);
 const [overallPercent, setOverallPercent] = useState(0);
 const abortRef = useRef(null);
 const [verificationStatus, setVerificationStatus] = useState(null);
 const [portfolioFiles, setPortfolioFiles] = useState([]);
 const [dragActive, setDragActive] = useState(false);
 const [pressCardFile, setPressCardFile] = useState(null);
 const [pressCardUploadLoading, setPressCardUploadLoading] = useState(false);

 useEffect(() => {
 loadVerificationStatus();
 }, []);

 const loadVerificationStatus = async () => {
 try {
 const response = await axios.get(`${API}/journalists/my-verification`);
 setVerificationStatus(response.data);
 } catch (error) {
 console.error("Failed to load verification status:", error);
 }
 };

 const addPortfolioUrl = () => {
 setFormData(prev => ({
 ...prev,
 portfolio_urls: [...prev.portfolio_urls, ""]
 }));
 };

 const removePortfolioUrl = (index) => {
 setFormData(prev => ({
 ...prev,
 portfolio_urls: prev.portfolio_urls.filter((_, i) =>i !== index)
 }));
 };

 const updatePortfolioUrl = (index, value) => {
 setFormData(prev => ({
 ...prev,
 portfolio_urls: prev.portfolio_urls.map((url, i) =>i === index ? value : url)
 }));
 };

 // File upload functions
 const handleFileUpload = async (files) => {
 setUploadLoading(true);
 const uploadedFiles = [];
 const filesArr = Array.from(files);
 const controller = new AbortController();
 abortRef.current = controller;
 const totalBytes = filesArr.reduce((s, f) =>s + f.size, 0) || 1;
 setProgressItems(filesArr.map(f => ({ name: f.name, loaded: 0, total: f.size, percent: 0, status: "pending" })));
 setOverallPercent(0);
 let bytesDoneBefore = 0;

 for (let i = 0; i < filesArr.length; i++) {
 const file = filesArr[i];
 try {
 // Validate file type
 const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'video/mp4', 'video/mov', 'application/pdf'];
 if (!allowedTypes.includes(file.type)) {
 showToast(`Неподдерживаемый тип файла: ${file.name}. Разрешены: JPG, PNG, MP4, MOV, PDF`, "error");
 setProgressItems(prev =>prev.map((p, idx) =>idx === i ? { ...p, status: "error", error: "Тип не поддерживается" } : p));
 continue;
 }
 
 // Validate file size (100MB max)
 if (file.size > 100 * 1024 * 1024) {
 showToast(`Файл ${file.name} превышает максимальный размер 100МБ`, "error");
 setProgressItems(prev =>prev.map((p, idx) =>idx === i ? { ...p, status: "error", error: ">100МБ" } : p));
 continue;
 }
 
 setProgressItems(prev =>prev.map((p, idx) =>idx === i ? { ...p, status: "uploading" } : p));

 // Upload file
 const formData = new FormData();
 formData.append('file', file);
 
 const response = await axios.post(`${API}/journalists/upload-portfolio`, formData, {
 headers: {
 'Content-Type': 'multipart/form-data'
 },
 signal: controller.signal,
 onUploadProgress: (evt) => {
 if (!evt.total) return;
 const loaded = evt.loaded || 0;
 const percent = Math.round((loaded / evt.total) * 100);
 setProgressItems(prev =>prev.map((p, idx) =>idx === i ? { ...p, loaded, total: evt.total, percent } : p));
 const overallLoaded = bytesDoneBefore + loaded;
 setOverallPercent(Math.round((overallLoaded / totalBytes) * 100));
 },
 });
 
 const portfolioItem = response.data.portfolio_item;
 uploadedFiles.push({
 id: portfolioItem.id,
 filename: portfolioItem.filename,
 content_type: portfolioItem.content_type,
 file_size: portfolioItem.file_size,
 quality_check: portfolioItem.quality_check,
 uploaded_at: new Date()
 });
 setProgressItems(prev =>prev.map((p, idx) =>idx === i ? { ...p, status: "done", percent: 100 } : p));
 bytesDoneBefore += file.size;
 setOverallPercent(Math.round((bytesDoneBefore / totalBytes) * 100));
 
 showToast(`Файл ${file.name} успешно загружен и проверен`, "success");
 } catch (error) {
 const aborted = controller.signal.aborted || error?.code === "ERR_CANCELED";
 setProgressItems(prev =>prev.map((p, idx) =>idx === i ? { ...p, status: "error", error: aborted ? "Отменено" : (error.response?.data?.detail || error.message) } : p));
 if (!aborted) {
 showToast(`Ошибка загрузки ${file.name}: ${error.response?.data?.detail || error.message}`, "error");
 } else {
 break;
 }
 }
 }
 
 abortRef.current = null;
 setPortfolioFiles(prev => [...prev, ...uploadedFiles]);
 setUploadLoading(false);
 };

 const cancelUpload = () => {
 if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
 };

 const handleDrop = (e) => {
 e.preventDefault();
 e.stopPropagation();
 setDragActive(false);
 
 const files = Array.from(e.dataTransfer.files);
 handleFileUpload(files);
 };

 const handleDrag = (e) => {
 e.preventDefault();
 e.stopPropagation();
 if (e.type === "dragenter" || e.type === "dragover") {
 setDragActive(true);
 } else if (e.type === "dragleave") {
 setDragActive(false);
 }
 };

 const removePortfolioFile = (fileId) => {
 setPortfolioFiles(prev =>prev.filter(file =>file.id !== fileId));
 };

 const getQualityIcon = (qualityCheck) => {
 if (!qualityCheck) return "";
 if (qualityCheck.quality_score >= 0.8) return "";
 if (qualityCheck.quality_score >= 0.5) return "";
 return "";
 };

 const getQualityText = (qualityCheck) => {
 if (!qualityCheck) return "Проверяется...";
 const score = qualityCheck.quality_score * 100;
 if (score >= 80) return `Отличное качество (${score.toFixed(0)}%)`;
 if (score >= 50) return `Приемлемое качество (${score.toFixed(0)}%)`;
 return `Низкое качество (${score.toFixed(0)}%)`;
 };

 const handlePressCardUpload = async (file) => {
 if (!file) return;
 const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
 if (!allowedTypes.includes(file.type)) {
 showToast('Разрешены только JPG, PNG или PDF для фото удостоверения', 'error');
 return;
 }
 if (file.size > 20 * 1024 * 1024) {
 showToast('Файл превышает 20МБ', 'error');
 return;
 }
 setPressCardUploadLoading(true);
 try {
 const fd = new FormData();
 fd.append('file', file);
 const res = await axios.post(`${API}/journalists/upload-portfolio`, fd, {
 headers: { 'Content-Type': 'multipart/form-data' }
 });
 setPressCardFile({ id: res.data.portfolio_item.id, filename: file.name, size: file.size });
 showToast('Фото удостоверения загружено', 'success');
 } catch (err) {
 showToast(err.response?.data?.detail || 'Ошибка загрузки фото', 'error');
 } finally {
 setPressCardUploadLoading(false);
 }
 };

 const handleSubmit = async (e) => {
 e.preventDefault();
 setLoading(true);

 try {
 const portfolioUrls = formData.portfolio_urls.filter(url =>url.trim() !== "");
 
 if (portfolioUrls.length === 0 && portfolioFiles.length === 0) {
 showToast("Необходимо загрузить образцы работ или указать ссылки на портфолио", "error");
 setLoading(false);
 return;
 }

 if (!formData.contact_email || !formData.contact_phone) {
 showToast("Email и телефон обязательны для связи", "error");
 setLoading(false);
 return;
 }

 const response = await axios.post(`${API}/journalists/verify`, {
 ...formData,
 portfolio_urls: portfolioUrls,
 portfolio_item_ids: portfolioFiles.map(f =>f.id),
 press_card_photo_id: pressCardFile?.id || null
 });
 
 showToast(response.data.message, "success");
 loadVerificationStatus();
 } catch (error) {
 showToast(error.response?.data?.detail || "Ошибка при отправке заявки", "error");
 } finally {
 setLoading(false);
 }
 };

 const getStatusColor = (status) => {
 switch (status) {
 case "approved": return "bg-green-100 text-green-800";
 case "pending": return "bg-yellow-100 text-yellow-800";
 case "under_review": return "bg-blue-100 text-blue-800";
 case "rejected": return "bg-red-100 text-red-800";
 default: return "bg-gray-100 text-gray-800";
 }
 };

 const getStatusText = (status) => {
 switch (status) {
 case "approved": return " Верифицирован";
 case "pending": return "На рассмотрении (1-3 дня)";
 case "under_review": return "Проверяется";
 case "rejected": return " Требуются доработки";
 case "not_requested": return "Не подавалась";
 default: return status;
 }
 };

 return (
 <div className="space-y-6">
 <UploadProgressBar
 uploads={progressItems}
 overallPercent={overallPercent}
 onCancel={cancelUpload}
 onClose={() => { setProgressItems([]); setOverallPercent(0); }}
 />
 <div className="flex items-center justify-between">
 <h2 className="text-2xl font-bold text-gray-900">Верификация журналиста</h2>
 <button
 onClick={onBack}
 className="text-blue-600 hover:text-blue-800"
 >
 ← Назад
 </button>
 </div>

 {/* Current Status */}
 {verificationStatus && (
 <div className="bg-white p-6 rounded-lg shadow-md">
 <h3 className="text-lg font-semibold mb-4">Статус верификации</h3>
 <div className="flex items-center space-x-3">
 <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(verificationStatus.status)}`}>
 {getStatusText(verificationStatus.status)}
 </span>
 {verificationStatus.verified && (
 <span className="text-green-600 font-medium">Верифицирован</span>
 )}
 </div>
 
 {verificationStatus.verification_notes && (
 <div className="mt-4 p-3 bg-gray-50 rounded-md">
 <p className="text-sm text-gray-700">
 <strong>Примечания:</strong> {verificationStatus.verification_notes}
 </p>
 </div>
 )}
 </div>
 )}

 {/* Verification Form */}
 {(!verificationStatus || verificationStatus.status === "not_requested" || verificationStatus.status === "rejected") && (
 <div className="bg-white p-6 rounded-lg shadow-md">
 <h3 className="text-lg font-semibold mb-4">Подать заявку на верификацию</h3>
 
 <form onSubmit={handleSubmit} className="space-y-6">
 {/* File Upload Section */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">Портфолио (загрузка файлов) *
 </label>
 <div 
 className={`border-2 border-dashed rounded-lg p-8 text-center ${
 dragActive ? "border-blue-500 bg-blue-50" : "border-gray-300"
 }`}
 onDragEnter={handleDrag}
 onDragLeave={handleDrag}
 onDragOver={handleDrag}
 onDrop={handleDrop}
 >
 <div className="text-gray-600 mb-4">
 <div className="text-4xl mb-2"></div>
 <p className="text-lg mb-2">Перетащите файлы сюда или</p>
 <input
 type="file"
 multiple
 accept=".jpg,.jpeg,.png,.mp4,.mov,.pdf"
 onChange={(e) =>handleFileUpload(Array.from(e.target.files))}
 className="hidden"
 id="portfolio-upload"
 />
 <label
 htmlFor="portfolio-upload"
 className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 cursor-pointer inline-block"
 >Выберите файлы
 </label>
 </div>
 <p className="text-sm text-gray-500">Поддерживаемые форматы: JPG, PNG, MP4, MOV, PDF (макс. 100МБ)
 </p>
 </div>
 
 {uploadLoading && (
 <div className="mt-4 text-center">
 <div className="text-blue-600">Загрузка и проверка качества...</div>
 </div>
 )}
 
 {/* Uploaded Files List */}
 {portfolioFiles.length > 0 && (
 <div className="mt-4 space-y-3">
 <h4 className="text-sm font-medium text-gray-700">Загруженные файлы:</h4>
 {portfolioFiles.map((file) => (
 <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
 <div className="flex items-center space-x-3">
 <span className="text-lg">{getQualityIcon(file.quality_check)}</span>
 <div>
 <p className="text-sm font-medium text-gray-900">{file.filename}</p>
 <p className="text-xs text-gray-500">
 {(file.file_size / 1024 / 1024).toFixed(1)}МБ | {getQualityText(file.quality_check)}
 </p>
 {file.quality_check && file.quality_check.resolution && (
 <p className="text-xs text-blue-600">{file.quality_check.resolution}</p>
 )}
 </div>
 </div>
 <button
 type="button"
 onClick={() =>removePortfolioFile(file.id)}
 className="text-red-600 hover:text-red-800 text-sm"
 >
 
 </button>
 </div>
 ))}
 </div>
 )}
 </div>

 {/* Portfolio URLs (as alternative) */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">Дополнительные ссылки на портфолио (опционально)
 </label>
 {formData.portfolio_urls.map((url, index) => (
 <div key={index} className="flex items-center space-x-2 mb-2">
 <input
 type="url"
 value={url}
 onChange={(e) =>updatePortfolioUrl(index, e.target.value)}
 className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
 placeholder="https://example.com/portfolio"
 />
 {formData.portfolio_urls.length > 1 && (
 <button
 type="button"
 onClick={() =>removePortfolioUrl(index)}
 className="text-red-600 hover:text-red-800"
 >
 
 </button>
 )}
 </div>
 ))}
 <button
 type="button"
 onClick={addPortfolioUrl}
 className="text-blue-600 hover:text-blue-800 text-sm"
 >
 + Добавить ссылку
 </button>
 </div>

 {/* Media Organization - теперь опциональная для штатных сотрудников */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Медиа организация <span className="text-gray-500">(только для штатных сотрудников СМИ)</span>
 </label>
 <input
 type="text"
 value={formData.media_organization}
 onChange={(e) =>setFormData({ ...formData, media_organization: e.target.value })}
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
 placeholder="Название СМИ или медиа компании"
 />
 </div>

 {/* Press Card */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Номер удостоверения журналиста
 </label>
 <input
 type="text"
 value={formData.press_card_number}
 onChange={(e) =>setFormData({ ...formData, press_card_number: e.target.value })}
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
 placeholder="Номер пресс-карты или удостоверения"
 />
 </div>

 {/* Press Card Photo Upload */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Фото удостоверения / пресс-карты <span className="text-gray-400 text-xs">(JPG, PNG или PDF, до 20МБ)</span>
 </label>
 {pressCardFile ? (
 <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-md">
 <div className="flex items-center gap-2 min-w-0">
 <FileText className="w-4 h-4 text-green-600 flex-shrink-0" />
 <span className="text-sm text-green-800 truncate">{pressCardFile.filename}</span>
 <span className="text-xs text-green-600">({(pressCardFile.size / 1024).toFixed(0)} КБ)</span>
 </div>
 <button
 type="button"
 onClick={() =>setPressCardFile(null)}
 className="ml-3 text-red-500 hover:text-red-700 text-sm"
 >
 <XIcon size={16} />
 </button>
 </div>
 ) : (
 <div>
 <input
 type="file"
 accept=".jpg,.jpeg,.png,.pdf"
 id="press-card-upload"
 className="hidden"
 onChange={(e) =>e.target.files[0] && handlePressCardUpload(e.target.files[0])}
 />
 <label
 htmlFor="press-card-upload"
 className={`flex items-center gap-2 px-4 py-2 border-2 border-dashed rounded-md cursor-pointer transition-colors ${
 pressCardUploadLoading ? 'border-blue-300 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
 }`}
 >
 <Upload size={16} className="text-gray-500" />
 <span className="text-sm text-gray-600">
 {pressCardUploadLoading ? 'Загрузка...' : 'Выбрать файл'}
 </span>
 </label>
 </div>
 )}
 </div>

 {/* Specializations */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">Специализация <span className="text-gray-400 text-xs">(выберите всё подходящее)</span>
 </label>
 <div className="flex flex-wrap gap-2">
 {SPECIALIZATION_OPTIONS.map(opt => (
 <button
 key={opt}
 type="button"
 onClick={() =>toggleArrayItem("specializations", opt)}
 className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
 formData.specializations.includes(opt)
 ? "bg-blue-600 text-white border-blue-600"
 : "bg-white text-gray-700 border-gray-300 hover:border-blue-400"
 }`}
 >
 {opt}
 </button>
 ))}
 </div>
 </div>

 {/* Equipment */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-2">Оборудование <span className="text-gray-400 text-xs">(выберите всё имеющееся)</span>
 </label>
 <div className="flex flex-wrap gap-2">
 {EQUIPMENT_OPTIONS.map(opt => (
 <button
 key={opt}
 type="button"
 onClick={() =>toggleArrayItem("equipment", opt)}
 className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
 formData.equipment.includes(opt)
 ? "bg-green-600 text-white border-green-600"
 : "bg-white text-gray-700 border-gray-300 hover:border-green-400"
 }`}
 >
 {opt}
 </button>
 ))}
 </div>
 </div>

 {/* Work Region */}
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Регион работы <span className="text-gray-400 text-xs">(город или несколько)</span>
 </label>
 <input
 type="text"
 value={formData.work_region}
 onChange={(e) =>setFormData({ ...formData, work_region: e.target.value })}
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
 placeholder="Например: Краснодар, Сочи, Новороссийск"
 />
 </div>

 {/* Contact Info - обязательные поля */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span>
 </label>
 <input
 type="email"
 required
 value={formData.contact_email}
 onChange={(e) =>setFormData({ ...formData, contact_email: e.target.value })}
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
 placeholder="your@email.com"
 />
 </div>
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Телефон <span className="text-red-500">*</span>
 </label>
 <input
 type="tel"
 required
 inputMode="tel"
 maxLength={18}
 value={formData.contact_phone}
 onChange={(e) =>setFormData({ ...formData, contact_phone: formatRuPhone(e.target.value) })}
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
 placeholder={RU_PHONE_PLACEHOLDER}
 data-testid="journalist-contact-phone-input"
 />
 </div>
 </div>

 {/* Editor Contact - для штатных сотрудников */}
 {formData.media_organization && (
 <div>
 <label className="block text-sm font-medium text-gray-700 mb-1">Контакт редактора <span className="text-gray-500">(для штатных сотрудников)</span>
 </label>
 <input
 type="text"
 value={formData.contact_person}
 onChange={(e) =>setFormData({ ...formData, contact_person: e.target.value })}
 className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
 placeholder="ФИО и телефон редактора"
 />
 </div>
 )}

 <button
 type="submit"
 disabled={loading}
 className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50"
 >
 {loading ? "Отправка заявки..." : "Отправить на проверку"}
 </button>
 </form>
 </div>
 )}

 {/* Information */}
 <div className="bg-blue-50 p-6 rounded-lg">
 <h4 className="font-medium text-blue-900 mb-3">Требования для верификации</h4>
 <ul className="text-sm text-blue-800 space-y-2">
 <li>• Загрузите портфолио: фото (JPG/PNG), видео (MP4/MOV), документы (PDF)</li>
 <li>• Автоматическая проверка качества: фото 12МП+, видео 4K+</li>
 <li>• Проверка на отсутствие водяных знаков</li>
 <li>• Подтверждение работы в медиа организации</li>
 <li>• Максимальный размер файла: 100МБ</li>
 <li>• Время рассмотрения заявки: 1-3 рабочих дня</li>
 </ul>
 <div className="mt-4 p-3 bg-white rounded-md">
 <p className="text-xs text-gray-600">
 <strong>Индикаторы качества:</strong>Отлично | Приемлемо | Требует улучшения
 </p>
 </div>
 </div>
 </div>
 );
};
